import React, { useState, useEffect, useRef, useCallback } from 'react';
import { SearchableDropdown, Button, SideBarModal } from '@/components';
import { Pitstop } from '@/services/api/pitstops';
import { hubPositionFrom } from '../hubPosition';
import { showErrorToast, showSuccessToast } from '@/components/toast/globalToast';
import {
  BookingScheduleSetting,
  BookingScheduleTimeWindow,
  updateBookingScheduleSetting,
} from '@/services/api/settings';
import { getUserEmail } from '@/utils';
import dayjs from 'dayjs';

interface ViewEditScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  setting: BookingScheduleSetting | null;
  pitstops: Pitstop[];
  onUpdated: () => void;
}

const DAYS_OF_WEEK = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
];

const DAY_FULL_LABELS: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  0: 'Sunday',
};

const generateTimeOptions = () => {
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 10) {
      const timeStr = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      options.push({ label: timeStr, value: timeStr });
    }
  }
  options.push({ label: '23:59', value: '23:59' });
  return options;
};
const TIME_OPTIONS = generateTimeOptions();

// ─── Shared display components (mirrors Issues page pattern) ────────────────

const FieldDisplay: React.FC<{
  label: string;
  value: string | React.ReactNode;
  className?: string;
  hasBorder?: boolean;
  labelClassName?: string;
}> = ({ label, value, className = '', hasBorder = true, labelClassName = '' }) => (
  <div className={`pb-2 ${className}`}>
    <label className={`block mb-1 ${labelClassName || 'heading-field'}`}>{label}</label>
    <div
      className={`text-sm font-medium text-gray-900 break-words pb-3 ${hasBorder ? 'border-b border-gray-200' : ''
        }`}
    >
      {value || '-'}
    </div>
  </div>
);

const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
  <div className="bg-gray-50 px-6 py-1 mb-4">
    <span className="font-normal rounded-t-lg heading-field tracking-wider">{title}</span>
  </div>
);

// ─── Types ───────────────────────────────────────────────────────────────────

interface EditFormData {
  hubID: number;
  activeDays: number[];
  timeWindows: BookingScheduleTimeWindow[];
  isActive: boolean;
}

// ─── Component ───────────────────────────────────────────────────────────────

export const ViewEditScheduleModal: React.FC<ViewEditScheduleModalProps> = ({
  isOpen,
  onClose,
  setting,
  pitstops,
  onUpdated,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<EditFormData>({
    hubID: 0,
    activeDays: [],
    timeWindows: [{ startTime: '', endTime: '' }],
    isActive: false,
  });

  const prefillForm = useCallback((s: typeof setting) => {
    if (!s) return;

    // Resolve hubID: use s.hubID if valid, else check alternate JSON keys, else match pitstop by name
    let resolvedHubID: number | string = s.hubID || (s as any).hub_id || (s as any).hubId || 0;
    if (!resolvedHubID || resolvedHubID === 0) {
      const sName = (s.hubName || '').toLowerCase().trim();
      if (sName) {
        const matched = pitstops.find((p) => {
          const pName = (p.name || '').toLowerCase().trim();
          return pName === sName || pName.includes(sName) || sName.includes(pName);
        });
        if (matched) resolvedHubID = matched.id || matched.ID || 0;
      }
    }

    // Ultimate fallback: if no ID could be resolved but we have a name, use the name as the value
    // so the dropdown can at least display the prefilled text properly instead of a blank placeholder.
    if ((!resolvedHubID || resolvedHubID === 0) && s.hubName) {
      resolvedHubID = s.hubName;
    }

    setFormData({
      hubID: resolvedHubID as number,
      activeDays: s.activeDays || [],
      timeWindows:
        s.timeWindows?.length > 0
          ? s.timeWindows
          : [{ startTime: '', endTime: '' }],
      isActive: Boolean(s.isActive),
    });
  }, [pitstops]);

  // Reset to view mode & populate form whenever modal opens with a new setting
  useEffect(() => {
    if (isOpen && setting) {
      setMode('view');
      prefillForm(setting);
    }
  }, [isOpen, setting, prefillForm]); // prefillForm changes when pitstops load

  const hubOptions = pitstops.map((p) => ({ value: p.id || p.ID || 0, label: p.name }));
  
  // Failsafe: Ensure formData.hubID actually exists in options, otherwise inject it
  if (formData.hubID && formData.hubID !== 0) {
    const exists = hubOptions.some((o) => o.value?.toString() === formData.hubID?.toString());
    if (!exists) {
      hubOptions.push({
        value: formData.hubID,
        label: setting?.hubName || String(formData.hubID)
      });
    }
  }

  const handleDayToggle = (dayValue: number) => {
    setFormData((prev) => {
      const isSelected = prev.activeDays.includes(dayValue);
      return {
        ...prev,
        activeDays: isSelected
          ? prev.activeDays.filter((d) => d !== dayValue)
          : [...prev.activeDays, dayValue],
      };
    });
  };

  const handleTimeWindowChange = (
    index: number,
    field: 'startTime' | 'endTime',
    value: string
  ) => {
    setFormData((prev) => {
      const newWindows = [...prev.timeWindows];
      newWindows[index] = { ...newWindows[index], [field]: value };
      return { ...prev, timeWindows: newWindows };
    });
  };

  const handleAddTimeWindow = () => {
    setFormData((prev) => ({
      ...prev,
      timeWindows: [...prev.timeWindows, { startTime: '', endTime: '' }],
    }));
  };

  const handleRemoveTimeWindow = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      timeWindows: prev.timeWindows.filter((_, i) => i !== index),
    }));
  };

  const isFormValid =
    formData.hubID !== 0 &&
    formData.activeDays.length > 0 &&
    formData.timeWindows.length > 0 &&
    formData.timeWindows.every((tw) => tw.startTime && tw.endTime);

  const handleSave = async () => {
    if (!setting) return;
    setIsSaving(true);
    try {
      const userEmail = getUserEmail();
      if (!userEmail) throw new Error('User email not found. Please log in again.');

      // Use toString() to handle both number/string hubID (SearchableDropdown returns string)
      let selectedPitstop = pitstops.find(
        (p) => (p.id || p.ID || 0).toString() === formData.hubID?.toString()
      );
      
      // If not found by ID, it might be the string fallback. Let's try to reverse map by name.
      if (!selectedPitstop && typeof formData.hubID === 'string') {
        const foundByName = pitstops.find((p) => p.name?.toLowerCase() === formData.hubID.toString().toLowerCase());
        if (foundByName) selectedPitstop = foundByName;
      }
      
      if (!selectedPitstop) {
        // If absolutely not found in active list, try to send what we got originally.
        selectedPitstop = {
          name: setting.hubName || String(formData.hubID),
          id: (typeof formData.hubID === 'number' ? formData.hubID : (setting.hubID || 0)) as number
        } as any;
      }

      await updateBookingScheduleSetting(setting.ID, {
        hubID: Number(selectedPitstop?.id || selectedPitstop?.ID || setting.hubID || 0),
        hubName: selectedPitstop?.name || setting.hubName,
        ...hubPositionFrom(selectedPitstop),
        activeDays: formData.activeDays,
        timeWindows: formData.timeWindows,
        isActive: formData.isActive,
        updatedBy: userEmail,
      });

      showSuccessToast('Booking operational updated', 'You can now use it in Booking');
      setMode('view');
      onUpdated();
    } catch (error) {
      console.error(error);
      const msg =
        error instanceof Error
          ? error.message
          : 'Failed to update Booking Operational — please try again';
      showErrorToast('Failed to update Booking Operational — please try again', msg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditClick = () => {
    prefillForm(setting); // ensure latest data is prefilled
    setMode('edit');
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, 0);
  };

  const handleClose = () => {
    setMode('view');
    onClose();
  };

  if (!isOpen || !setting) return null;

  /* ─── VIEW MODE ──────────────────────────────────────────────────────────── */
  const viewContent = (
    <div className="flex-1">
      <div
        className="bg-gray-50 px-6 py-2 font-normal uppercase tracking-wider border-b border-gray-200 heading-field"
        style={{ fontSize: '12px' }}
      >
        Booking Operational Detail
      </div>
      <div className="p-6 pb-0">
        <FieldDisplay label="Hub Location" value={setting.hubName || '-'} className="mb-1" />

        <FieldDisplay
          label="Hari & Tanggal Pengambilan Motor"
          value={
            (setting.activeDays || []).length > 0
              ? (setting.activeDays || []).map((d) => DAY_FULL_LABELS[d] || String(d)).join(', ')
              : '-'
          }
          className="mb-1"
        />

        <FieldDisplay
          label="Jam Pengambilan Motor (WIB)"
          value={
            (setting.timeWindows || []).length > 0
              ? setting.timeWindows.map(tw => `${tw.startTime} – ${tw.endTime} WIB`).join(', ')
              : '-'
          }
          className="mb-1"
        />

        <FieldDisplay
          label="Status"
          value={
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${setting.isActive ? 'bg-[#1BB55C] text-white' : 'bg-gray-500 text-white'
                }`}
            >
              {setting.isActive ? 'Active' : 'Inactive'}
            </span>
          }
          hasBorder={false}
          className="mb-0"
        />
      </div>

      {/* Info section */}
      <SectionHeader title="INFO" />
      <div className="p-6 pt-0">
        <div className="grid grid-cols-2 gap-x-6">
          <FieldDisplay
            label="Created By"
            value={setting.createdBy || '-'}
            className=""
            hasBorder={false}
          />
          <FieldDisplay
            label="Created Date"
            value={
              setting.createdAt
                ? dayjs(setting.createdAt).format('MMMM D, YYYY [at] HH.mm')
                : '-'
            }
            className="mb-0"
            hasBorder={false}
          />
        </div>
        <div className="border-t border-gray-200 mb-4" />
        <div className="grid grid-cols-2 gap-x-6">
          <FieldDisplay
            label="Last Updated By"
            value={setting.updatedBy || '-'}
            className="mb-0"
            hasBorder={false}
          />
          <FieldDisplay
            label="Last Updated"
            value={
              setting.updatedAt
                ? dayjs(setting.updatedAt).format('MMMM D, YYYY [at] HH.mm')
                : '-'
            }
            className="mb-0"
            hasBorder={false}
          />
        </div>
      </div>
    </div>
  );

  /* ─── EDIT MODE ──────────────────────────────────────────────────────────── */
  const editContent = (
    <div className="flex-1">
      <div
        className="bg-gray-50 px-6 py-2 font-medium uppercase tracking-wider border-b border-gray-200 heading-field"
        style={{ fontSize: '12px' }}
      >
        Booking Operational Detail
      </div>

      <div className="p-6 pb-40">
        {/* Hub Location */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Hub Location <span className="text-red-500">*</span>
          </label>
          <SearchableDropdown
            options={hubOptions}
            value={formData.hubID}
            onChange={(val) =>
              setFormData((prev) => ({
                ...prev,
                hubID: typeof val === 'number' ? val : Number(val),
              }))
            }
            placeholder="Hub Location"
            className="w-full"
          />
        </div>

        {/* Active Days */}
        <div className="mb-5">
          <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
            Hari &amp; Tanggal Pengambilan Motor{' '}
            <span className="text-red-500 ml-1">*</span>
            <i className="ri-information-2-fill text-gray-400 ml-1"></i>
          </label>
          <div className="flex flex-wrap gap-2">
            {DAYS_OF_WEEK.map((day) => {
              const isSelected = formData.activeDays.includes(day.value);
              return (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => handleDayToggle(day.value)}
                  className={`px-2 py-0.5 rounded-full text-xs font-medium border transition-colors ${isSelected
                      ? 'bg-[#5e2aac] text-white border-[#5e2aac]'
                      : 'bg-white text-[#5e2aac] border-[#5e2aac] hover:bg-gray-50'
                    }`}
                >
                  {day.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Time Windows */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Jam Pengambilan Motor (WIB) <span className="text-red-500">*</span>
          </label>
          <div className="space-y-3 mb-3">
            {formData.timeWindows.map((tw, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="flex-1">
                  <SearchableDropdown
                    options={TIME_OPTIONS}
                    value={tw.startTime}
                    onChange={(val) =>
                      handleTimeWindowChange(index, 'startTime', String(val))
                    }
                    placeholder="Start Time"
                    allowClear={false}
                    className="w-full"
                  />
                </div>
                {tw.startTime && (
                  <div className="flex-1">
                    <SearchableDropdown
                      options={TIME_OPTIONS}
                      value={tw.endTime}
                      onChange={(val) =>
                        handleTimeWindowChange(index, 'endTime', String(val))
                      }
                      placeholder="End Time"
                      allowClear={false}
                      className="w-full"
                    />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => handleRemoveTimeWindow(index)}
                  disabled={formData.timeWindows.length === 1}
                  className="flex-shrink-0 h-10 w-10 flex items-center justify-center border border-gray-300 rounded-xl text-red-500 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            ))}
          </div>
          {formData.timeWindows.length < 3 && (
            <button
              type="button"
              onClick={handleAddTimeWindow}
              className="w-full py-2.5 border border-gray-200 rounded-md text-sm text-gray-400 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
            >
              <i className="ri-add-line"></i> Add Jam Pengambilan Motor
            </button>
          )}
        </div>

        {/* Status */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${formData.isActive ? 'bg-[#5e2aac]' : 'bg-gray-200'
                }`}
              onClick={() =>
                setFormData((prev) => ({ ...prev, isActive: !prev.isActive }))
              }
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.isActive ? 'translate-x-5' : 'translate-x-0'
                  }`}
              />
            </button>
            <span className="text-[13px] text-gray-700">
              {formData.isActive ? 'Active' : 'Not Active'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <SideBarModal
      isOpen={isOpen}
      onClose={handleClose}
      position="right"
      width="sm"
      contentRef={scrollRef}
      header={
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Edit Booking Operational Setting
          </h2>
          <p className="text-sm text-gray-600">Update Booking Operational Setting.</p>
        </div>
      }
      footer={
        mode === 'view' ? (
          // Issues-style Edit button: purple tint background
          <button
            className="w-full py-3 rounded-md font-medium text-sm transition-colors"
            onClick={handleEditClick}
            style={{
              backgroundColor: '#784DEF1A',
              color: '#5E2AAC',
              border: 'none',
            }}
          >
            Edit
          </button>
        ) : (
          <Button
            variant="primary"
            onClick={handleSave}
            className="w-full py-3 bg-[#5e2aac] hover:bg-[#4a2287]"
            disabled={!isFormValid || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        )
      }
    >
      {mode === 'view' ? viewContent : editContent}
    </SideBarModal>
  );
};
